<template>
    <div v-if="vehicle" class="booking-request-form-old">
        <i18n
            v-if="nightlyPrice"
            class="booking-request-form-old__price"
            path="booking_request_form.nightly_price"
            tag="div"
        >
            <template #from>{{ $t('common.from_capitalized') }}</template>
            <template #price>
                <strong>{{ nightlyPrice }}</strong>
            </template>
            <template #perNight>{{ $t('common.per_night') }}</template>
        </i18n>

        <div v-if="renterCanNotBookBaloiseVehicle" class="booking-request-form-old__content">
            <Notice type="warning">
                {{ $t('pages.booking_request.baloise_vehicle_renting_disallowed_notice') }}
            </Notice>
            <button
                class="button button--primary booking-request-form-old__book-button button--full-width"
                @click="applyFilter"
            >
                {{ $t('modals.insurance_limitation.show_available_vehicles') }}
            </button>
        </div>
        <div v-else class="booking-request-form-old__content">
            <div class="booking-request-form-old__dates">
                <label class="label">
                    <template v-if="selectedDates.from">
                        {{ $t('booking_request_form.dates_selected_label') }}
                    </template>
                    <template v-else>
                        {{ $t('booking_request_form.date_select_label') }}
                    </template>
                </label>
                <DateSelectInput
                    :init-from="selectedDates.from"
                    :init-to="selectedDates.to"
                    :disabled="!featureEnabled('renterAccess')"
                    :disabled-dates="vehicle.unavailableDates"
                    :high-season-min-booking-length="vehicle.minBookingLengthHigh"
                    :low-season-min-booking-length="vehicle.minBookingLengthLow"
                    :has-tooltips="true"
                    :placeholder="$t('booking_request_form.date_select_placeholder')"
                    :add-to-query-immediately="true"
                />
                <div
                    v-if="selectedDates.from && selectedDates.to && $v.selectedDates.$invalid"
                    class="booking-request-form-old__error"
                >
                    <Notice v-if="!$v.selectedDates.from.validRange || !$v.selectedDates.to.validRange" type="warning">
                        {{ $t('booking_request_form.error_invalid_range') }}
                    </Notice>
                    <Notice v-if="!$v.selectedDates.from.minLength || !$v.selectedDates.to.minLength" type="warning">
                        {{ $t('booking_request_form.error_too_short', { minLength: minBookingLengthForDates }) }}
                    </Notice>
                    <Notice v-if="!$v.selectedDates.from.maxLength || !$v.selectedDates.to.maxLength" type="warning">
                        {{ $t('booking_request_form.error_too_long', { maxLength: 90 }) }}
                    </Notice>
                </div>
            </div>

            <VehiclePricing
                v-if="!$v.selectedDates.$invalid"
                class="booking-request-form-old__price-info"
                :vehicle="vehicle"
                :show-deposit="vehicle.pricing ? !vehicle.pricing.cashDeposit : false"
                :show-num-of-nights="true"
                :is-loading="isLoadingPrice"
                :load-pricing-error="loadPricingError"
                :pricing="vehicle.pricing"
                :from-date="selectedDates.from"
                :to-date="selectedDates.to"
            />

            <Notice v-if="notAllowedToRentError" type="warning" class="booking-request-form-old__disabled-message">
                {{ notAllowedToRentError }}
            </Notice>

            <div v-if="!isLoggedIn || isRenter" id="booking-form-btn-wrapper">
                <button
                    v-if="selectedDates.from"
                    :disabled="$v.selectedDates.$invalid || !featureEnabled('renterAccess')"
                    class="button button--primary booking-request-form-old__book-button button--full-width"
                    @click="$emit('click')"
                >
                    <template v-if="vehicle.bookingMethod === 'instant'">
                        {{ $t('booking_request_form.book_instantly') }}
                    </template>
                    <template v-else>{{ $t('booking_request_form.send_request_button') }}</template>
                </button>
                <button
                    v-else
                    class="button button--primary booking-request-form-old__book-button button--full-width"
                    @click="$root.$emit('focus-datepicker')"
                >
                    {{ $t('booking_request_form.select_dates_button') }}
                </button>
            </div>

            <Notice v-if="isOwner" :show-icon="true" type="info" class="booking-request-form-old__disabled-message">
                {{ $t('booking_request_form.owner_notice') }}
            </Notice>
            <RenterAccessSignupForm v-else-if="!featureEnabled('renterAccess')" :country="vehicle.country" />
            <div v-else class="booking-request-form-old__footnote">
                {{ $t('booking_request_form.footnote_not_binding') }}<br />
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { validationMixin } from 'vuelidate';
import { required } from 'vuelidate/lib/validators';

import { getFilterValue, buildQuery } from '~/helpers/queryParser';
import { AVAILABLE_BETWEEN, RENTABLE_OUTSIDE_OF_SWITZERLAND } from '~/config/filterKeys';

import DateSelectInput from '~/components/form-elements/DateSelectInput.vue';
import Notice from '~/components/Notice.vue';
import VehiclePricing from '~/components/vehicles/VehiclePricing.vue';
import RenterAccessSignupForm from '~/components/onboarding/RenterAccessSignupForm.vue';

export default {
    components: {
        DateSelectInput,
        Notice,
        VehiclePricing,
        RenterAccessSignupForm,
    },
    mixins: [validationMixin],
    validations: {
        // NOTE: This code is duplicated in the BookingRequest view
        selectedDates: {
            from: {
                required,
                validRange() {
                    return this.$validators.isValidRange(
                        this.vehicle.unavailableDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                minLength() {
                    return this.$validators.isRangeLongEnough(
                        this.minBookingLengthForDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                maxLength() {
                    return this.$validators.isRangeShorterThanMax(this.selectedDates.from, this.selectedDates.to);
                },
            },
            to: {
                required,
                validRange() {
                    return this.$validators.isValidRange(
                        this.vehicle.unavailableDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                minLength() {
                    return this.$validators.isRangeLongEnough(
                        this.minBookingLengthForDates,
                        this.selectedDates.from,
                        this.selectedDates.to
                    );
                },
                maxLength() {
                    return this.$validators.isRangeShorterThanMax(this.selectedDates.from, this.selectedDates.to);
                },
            },
        },
    },
    props: {
        vehicle: {
            type: Object,
            default: null,
        },
        isLoadingPrice: {
            type: Boolean,
            default: false,
        },
        loadPricingError: {
            type: Boolean,
            default: false,
        },
        notAllowedToRentError: {
            type: String,
            default: null,
        },
        renterCanNotBookBaloiseVehicle: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        ...mapGetters(['user', 'isLoggedIn', 'isOwner', 'isRenter']),
        selectedDates() {
            return getFilterValue(this.$route.query, AVAILABLE_BETWEEN);
        },
        nightlyPrice() {
            if (this.selectedDates?.from && !this.renterCanNotBookBaloiseVehicle) {
                return;
            }

            const { formatAsPrice, price } = this.$options.filters;
            const nightlyPrice = this.vehicle.pricing.lowNightlyPrice;
            const nightlyPriceFormatted = formatAsPrice(price(nightlyPrice), this.vehicle.pricing.currency, 0);

            return nightlyPriceFormatted;
        },
        minBookingLengthForDates() {
            // NOTE: This code is duplicated in the BookingRequest view
            const { from, to } = this.selectedDates;

            if (this.$utils.isOnlyHighSeason(from, to)) {
                return this.vehicle.minBookingLengthHigh;
            }

            if (this.$utils.isOnlyLowSeason(from, to)) {
                return this.vehicle.minBookingLengthLow;
            }

            // Mixed season, take the "loosest" rule
            return Math.min(this.vehicle.minBookingLengthHigh, this.vehicle.minBookingLengthLow);
        },
    },
    methods: {
        applyFilter() {
            const query = buildQuery(this.$route.query, { [RENTABLE_OUTSIDE_OF_SWITZERLAND]: true });

            this.$router.push(this.localePath({ path: this.$utils.getVehicleSearchPagePath(this.vehicle), query }));
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';
@import '@/sass/blocks/_form.scss';

.booking-request-form-old {
    box-sizing: border-box;
    width: 100%;
    text-align: left;
    border: 1px solid $primary;
    border-radius: $default-border-radius;

    &__content {
        padding: 30px;
    }

    &__price {
        padding: 24px 30px;
        border-bottom: 1px solid $primary;
    }

    &__dates {
        label {
            margin-bottom: 16px;
        }
    }

    &__error {
        margin-top: 10px;
    }

    &__price-info {
        margin: 16px auto 0;
    }

    &__book-button {
        margin-top: 16px;
        box-sizing: border-box;
    }

    .renter-access-signup {
        margin-top: 20px;
    }

    &__disabled-message {
        margin-top: 20px;
    }

    &__footnote {
        @include font-size(12px);
        text-align: center;
        margin-top: 16px;
        line-height: 1.5em;
    }
}
</style>
